import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { TbTrash } from "react-icons/tb";
import { isTodayOrFuture } from "./SchedulerHelpers";

export default function PortalMenu({
  menuPos,
  onClose,
  items,
  onSelect,
  onDelete,
  updateLoading,
  reservationLoading,
  selectedDate,
}) {
  const ref = useRef(null);
  const menuClickFlag = useRef(false);
  const markMenuClick = () => {
    menuClickFlag.current = true;
  };
  const stopAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // stop native bubbling & other listeners in the same phase
    if (e.nativeEvent?.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
    markMenuClick();
  };

  function pickPlacement(btnRect, menuW, menuH, padding = 8, gap = 6) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const fits = (x, y) =>
      x >= padding &&
      y >= padding &&
      x + menuW <= vw - padding &&
      y + menuH <= vh - padding;

    const candidates = [
      {
        name: "bottom-end",
        x: Math.min(btnRect.right - menuW, vw - menuW - padding),
        y: btnRect.bottom + gap,
      },
      {
        name: "top-end",
        x: Math.min(btnRect.right - menuW, vw - menuW - padding),
        y: btnRect.top - menuH - gap,
      },
      {
        name: "right-start",
        x: btnRect.right + gap,
        y: Math.max(btnRect.top, padding),
      },
      {
        name: "left-start",
        x: btnRect.left - menuW - gap,
        y: Math.max(btnRect.top, padding),
      },
    ];

    for (const c of candidates) if (fits(c.x, c.y)) return c;

    return {
      name: "clamped",
      x: Math.min(
        Math.max(btnRect.right - menuW, padding),
        vw - menuW - padding
      ),
      y: Math.min(
        Math.max(btnRect.bottom + gap, padding),
        vh - menuH - padding
      ),
    };
  }

  // After render, measure actual size and re-place if needed
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const { x, y, placement } = menuPos;

    // If it overflows, recompute using real width/height
    const vw = window.innerWidth,
      vh = window.innerHeight,
      pad = 8;
    const overflows =
      rect.left < pad ||
      rect.top < pad ||
      rect.right > vw - pad ||
      rect.bottom > vh - pad;

    if (overflows) {
      const btnRectGuess = {
        // approximate from current top-right
        left: rect.right - rect.width,
        right: rect.right,
        top: rect.top,
        bottom: rect.top,
      };
      const fit = pickPlacement(btnRectGuess, rect.width, rect.height, pad, 6);
      el.style.left = `${fit.x}px`;
      el.style.top = `${fit.y}px`;
      el.dataset.placement = fit.name;
    } else {
      el.dataset.placement = placement;
    }
  }, [menuPos]);

  const isFutureDate = useMemo(() => isTodayOrFuture(selectedDate));

  useEffect(() => {
    const close = (e) => {
      // Ignore clicks inside the menu
      if (e.type === "mousedown" && e.target.closest(".ev-menu-popover"))
        return;
      onClose();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", onEsc);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const style = {
    position: "fixed",
    left: menuPos.x,
    top: menuPos.y,
    zIndex: 10000,
    width: 200,
    background: "#fff",
    color: "#111827",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    boxShadow:
      "0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -2px rgba(0,0,0,.05)",
    padding: 6,
  };

  const menu = (
    <div
      className="ev-menu-popover ev-menu"
      ref={ref}
      style={style}
      onPointerDown={stopAll}
      onMouseDown={stopAll}
      onClick={stopAll}
    >
      <div className="ev-menu-group-label">Set status</div>
      {items
        .filter(
          ([label]) =>
            !(
              (isFutureDate && (label === "Seated" || label === "Completed")) ||
              label === "Modified"
            )
        )
        .map(([label, c]) => (
          <button
            key={label}
            className="ev-menu-item"
            disabled={updateLoading || reservationLoading}
            onClick={(e) => {
              stopAll(e);
              onSelect(label);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "8px 10px",
              border: "none",
              background: "transparent",
              borderRadius: 6,
            }}
          >
            <span
              className="ev-menu-dot"
              style={{
                width: 12,
                height: 12,
                borderRadius: 9999,
                background: c.text,
                border: `1px solid ${c.text}`,
              }}
            />
            {label}
          </button>
        ))}

      {typeof onDelete === "function" && (
        <>
          <div className="ev-menu-divider" role="separator" />
          <button
            type="button"
            className="ev-menu-item ev-menu-item--danger"
            disabled={updateLoading || reservationLoading}
            onClick={(e) => {
              stopAll(e);
              onDelete();
              onClose();
            }}
          >
            <TbTrash aria-hidden />
            Delete
          </button>
        </>
      )}
    </div>
  );

  return createPortal(menu, document.body);
}
